import { PermissionString } from "@cervval.js/common-permissions/src/dtos.permissions";
import { ValueOrFunction } from "@cervval.js/common/src/tools/object.tools";

export const DEFAULT_DROPDOWN_SECTIONS = { "main": "button", "dropDown": "dropdownMenu" };

/**
 * The menu item definition contains all needed information to create of the HtmlElement
 */
export interface MenuItemDefinition {
    /** icon html - exclusive with iconClass */
    iconHtml?: string | ((menuUid: string) => string);
    /** icon class (usually font awesome) - exclusive with iconHtml */
    iconClasses?: string;
    /** display name of the page */
    label: ValueOrFunction<string>;

    /** Url of the page (href of item menus). For pages navigation service will provide the url if none is provided */
    url?: string;
}

/**
 * The MenuItemInsertion determines where the menu item will be inserted within the menu, it is used in PageDefinition
 */
export interface MenuItemInsertion<MenuUid = string> {
    /** the menu where item must be inserted */
    menuUid: MenuUid;
    /** the section if menu has several sections */
    sectionUid?: string;
    /** group number is used to put separator : we put separator between each defined group. If not define, we assume it's 0*/
    groupNumber?: number;
    /** order in the menu / section / groupNumber -- if no order is defined or if two items have the same order value, the items are sorted in alphabetic order */
    order?: number;
    /** override definition for this menu insertion */
    item?: MenuItemDefinition;
}

/**
 * The MenuDefinition contains information how to get the menu and its style
 */
export interface MenuDefinition<MenuPermissionString extends PermissionString = PermissionString> {

    /** Ref in DOM (if different from menuUid) */
    menuRef?: string;

    /** A dynamic menu is displayed only if the current page is referenced by the menu */
    isDynamic?: boolean;

    /** Menu factories can be used to override how items and separators are created. If no uid provided, the 'default' factory will be used */
    menuFactoryUid?: string;

    /**
     * Sections are mainly used for dynamic menus. The HtmlElement referenced by menuRef is shown / hidden
     * and items are injected at another reference within the menu
     */
    sections?: { [sectionUid: string]: string /** ref to a div container */ };

    dynamicParentReference?: {
        /** Uid of the parent menu */
        menuUid: string,
        /** Ref to the li element of the navbar */
        activeRef: string
    };

    /**
     * Item that is always visible but that is active if this dynamic menu is visible
     */
    dynamicMenuStaticItem?: {
        /** Insertion of item with mandatory default url */
        insertion: MenuItemInsertion & { item: MenuItemDefinition & { url: string } },
        /** The menu item will be inserted and the link inside the element will change its target to the active page every time the menu is set active */
        dynamicUrl: boolean
    };

    /** 
     * If menuRef is a CjsDropDown, ou can set dynamicUrl, clicking on the button will navigate to the last navigated page of this menu 
     * or the defaultUrl if no navigation was done before
     */
    dynamicUrl?: { defaultUrl: string };

    /**
     * To use with CjsDropDown, define the main item style
     */
    staticMainItem?: Omit<MenuItemDefinition, "url">; // Omit url to avoid conflict with dynamicUrl 

    /**
     * To reach the page, user needs to have all permissions. If there is an array within permissions, user needs to have at least one of the permissions 
     * ex : permissions=["P1",["P2","P3"]] => hasPermission(permissions) = hasPermission("P1") && (hasPermission("P2") || hasPermission("P3"))
     */
    permissions?: ValueOrFunction<(MenuPermissionString | MenuPermissionString[])[]>;

    /**
     * Module name (modules defined in your application) that need to be enabled to show the menu, cf NavigationService for the callback to define the active modules.
     */
    neededModuleNames?: string[];
}

/**
 * MenuDefinition by menuUid
 */
export type MenuDefinitions = Record<string, MenuDefinition>;
